import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import moment from 'moment-timezone';

import { PaymentPlanCodes } from '@shared/price-plan/enums';
import { SubscriptionState } from '@shared/recurly/enums';
import { SubscriptionTypes } from '@shared/restaurant/enums';
import { AccountStatus } from '@shared/account/enum';
import { AuthUser } from '@shared/signup/models';
import { AccountAccess, IStatus } from './status.interfaces';
import { IAlertTypes } from '@tablesready/frontend-alerts-bar';

@Injectable({ providedIn: 'root' })
export class StatusService {
  readonly status$: Observable<Record<string, IStatus>>;

  private accountAccess: AccountAccess[] = [];
  private readonly statusSource$ = new BehaviorSubject<Record<string, IStatus>>({});

  constructor() {
    this.status$ = this.statusSource$.asObservable();
  }

  setAccess(access: AccountAccess[]): void {
    this.accountAccess = access;
  }

  clearAllStatuses(): void {
    this.statusSource$.next({});
  }

  clearStatusById(statusId: string): void {
    const currentStatus = this.statusSource$.getValue();
    currentStatus[statusId] = undefined;

    this.statusSource$.next(currentStatus);
  }

  add(opts: IStatus): void {
    const currentStatus = this.statusSource$.getValue();
    currentStatus[opts.id] = opts;

    return this.statusSource$.next(currentStatus);
  }

  updateStatusBarAlerts(user: AuthUser): void {
    this.clearAllStatuses();

    const location = user.location;
    const account = user.account;
    const expirationStatus = this.isCardExpired(user);
    const hasInvites = this.accountAccess.filter(accountAccess => Boolean(accountAccess.inviteId) &&
      accountAccess.fromAccountId && user.account?.id !== accountAccess.fromAccountId).length > 0;

    if (hasInvites) {
      this.add({
        type: 'alert-info',
        dismissable: false,
        id: 'invites-available',
        body: 'You have unchecked invites to other accounts. Please check your invites on your <b><a class="alert-link" href="/settings/account/profile?accountsAccessTab=true">profile page</a></b>.',
      });
    }

    if (account?.subscriptionType !== SubscriptionTypes.recurly || !location.recurly || !account.recurly) {
      return;
    }

    const isPaid = location.recurly.planCode !== PaymentPlanCodes.Free;

    if (location.recurly.planCode === PaymentPlanCodes.Free) {
      let status: IStatus = {
        type: IAlertTypes.warning,
        dismissable: false,
        id: 'free-account-verify-email',
        body: `<b>Check your email</b> to verify your account and enable messaging. <a href="/verify?username=${user.account.id}" class="alert-link">Resend your verification email</a>.`,
      };

      if (user.account.status !== AccountStatus.unverified) {
        const linkText = user.account.recurly.trial === undefined ? 'Try a paid plan free for 14 days' : 'Upgrade today';
        const message = user.account.recurly.trial === undefined
          ? `${location.usage.limit - location.usage.total} messages remaining this month.`
          : `You’ve lost access to your paid plan features. ${location.usage.limit - location.usage.total} messages remaining this month.`;
        status.type = 'alert-dark';
        status.body = `<b>Free plan.</b> ${message} <a href="/settings/location/billing" class="alert-link">${linkText}</a>.`;
      }

      this.add(status);
    }

    if (user.account.status === AccountStatus.unverified && user.location.recurly.planCode !== PaymentPlanCodes.Free) {
      this.add({
        type: IAlertTypes.warning,
        dismissable: false,
        id: 'free-trial-account-verify-account',
        body: `<b>Check your email</b> to verify your account and enable messaging. <a href="/verify?username=${user.account.id}" class="alert-link">Resend your verification email</a>.`,
      });
    }

    if (location.recurly.trial && user.location.recurly.planCode !== PaymentPlanCodes.Free) {
      const oneDay = 1000 * 60 * 60 * 24;
      const currentUserPlan = user.account.recurly.subscriptions.find(subscription => subscription.uuid === location.recurly.subscriptionUuid);
      let expiresIn = -1;

      if (currentUserPlan !== undefined) {
        expiresIn = Math.round((currentUserPlan.expiration_date - Date.now()) / oneDay);
      }

      this.add({
        type: 'alert-dark',
        dismissable: false,
        id: 'free-trial-account',
        body: `<b>Free trial account.</b> Expires in ${expiresIn} days, with ${location.usage.limit - location.usage.total} messages remaining. <a href="/settings/location/billing" class="alert-link">Subscribe today</a>`
      });
    }

    if (location.recurly.subscription_state === SubscriptionState.expired as SubscriptionState) {
      this.add({
        type: IAlertTypes.danger,
        dismissable: false,
        id: 'your-subscription-has-expired',
        body: '<b>Your subscription has expired.</b> <a href="/settings/location/billing" class="alert-link">Create a new one</a> now.',
      });
    }

    if (location.recurly.subscription_state === SubscriptionState.paused as SubscriptionState) {
      this.add({
        type: IAlertTypes.danger,
        dismissable: false,
        id: 'your-subscription-has-been-paused',
        body: '<b>Your subscription has been paused.</b> <a href="/settings/location/billing" class="alert-link">Reactivate subscription</a> now.',
      });
    }

    if (expirationStatus === SubscriptionState.expired) {
      this.add({
        type: IAlertTypes.danger,
        dismissable: true,
        id: 'your-credit-card-has-expired',
        body: '<b>Your credit card has expired.</b> Please <a href="/settings/account/billing" class="alert-link">update your billing information</a> soon.',
      });
    }

    if (expirationStatus === SubscriptionState.expiring) {
      this.add({
        type: IAlertTypes.warning,
        dismissable: true,
        id: 'your-credit-card-will-expire-soon',
        body: '<b>Your credit card will expire soon.</b> Please <a href="/settings/account/billing" class="alert-link">update your billing information</a> soon.',
      });
    }
  }

  getDaysLeft(date) {
    if (!date) {
      return 0;
    }
    const msPerMin = 60000;
    const expires = new Date(date).getTime();
    const now = Date.now();
    const diff = expires - now;
    return moment.duration(diff / msPerMin, 'minutes').humanize(true);
  }

  isCardExpired(user) {
    if (
      user.recurly &&
      user.recurly.card_expires &&
      user.recurly.subscription_state === SubscriptionState.active
    ) {
      const arr_data = user.recurly.card_expires.split('/');
      const year = arr_data[1];
      const month = arr_data[0];

      if (Number(year) < Number(new Date().getFullYear())) {
        return SubscriptionState.expired;
      }
      if (Number(year) === Number(new Date().getFullYear())) {
        if (Number(month) < Number(new Date().getMonth()) + 1) {
          return SubscriptionState.expired;
        }

        if (Number(month) === Number(new Date().getMonth()) + 1) {
          return SubscriptionState.expiring;
        }
      }
    }

    return 'ok';
  }
}
